import React, {
  ChangeEvent,
  useRef,
} from 'react';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import IconButton from 'core/components/IconButton';
import {
  downloadFileFromUrl,
} from 'pages/Dashboard/utils/helper';
import {
  PdfTemplateDocument,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import HowToDrawCard from 'views/EMR/Settings/PdfTemplateBuilder/components/HowToDrawCard';
import head from 'lodash/head';
import isNil from 'lodash/isNil';

type Props = {
  document: PdfTemplateDocument;
  isReplacingDocument?: boolean;
  canReplaceDocument?: boolean;
  fieldHistoryLabel?: string | null;
  canUndoFieldChange?: boolean;
  canRedoFieldChange?: boolean;
  onUndoFieldChange?: () => void;
  onRedoFieldChange?: () => void;
  onReplaceDocument: (file: File) => Promise<void>;
};

export default function DocumentPanel({
  document,
  isReplacingDocument = false,
  canReplaceDocument = true,
  canUndoFieldChange = false,
  canRedoFieldChange = false,
  onUndoFieldChange,
  onRedoFieldChange,
  onReplaceDocument,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelection = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = head(event.target.files);
    event.target.value = '';

    if (!isNil(file)) {
      await onReplaceDocument(file);
    }
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex flex-col gap-4 px-4 py-5">
        <div className="flex items-center gap-1 -mt-4">
          <span className={clsx(
            'inline-flex',
            !canUndoFieldChange && 'cursor-not-allowed',
          )}
          >
            <IconButton
              size="small"
              tooltip="Undo (Ctrl/Cmd+Z)"
              hideTooltip={!canUndoFieldChange}
              disabled={!canUndoFieldChange}
              className={clsx(
                '!h-8 !w-8 !rounded-md !border !bg-white',
                canUndoFieldChange
                  ? '!border-gray-200 !text-gray-600 hover:!bg-gray-50'
                  : '!border-gray-100 !text-gray-300',
              )}
              onClick={() => {
                onUndoFieldChange?.();
              }}
            >
              <UndoOutlinedIcon className="!h-4.5 !w-4.5" />
            </IconButton>
          </span>
          <span className={clsx(
            'inline-flex',
            !canRedoFieldChange && 'cursor-not-allowed',
          )}
          >
            <IconButton
              size="small"
              tooltip="Redo (Ctrl/Cmd+Shift+Z)"
              hideTooltip={!canRedoFieldChange}
              disabled={!canRedoFieldChange}
              className={clsx(
                '!h-8 !w-8 !rounded-md !border !bg-white',
                canRedoFieldChange
                  ? '!border-gray-200 !text-gray-600 hover:!bg-gray-50'
                  : '!border-gray-100 !text-gray-300',
              )}
              onClick={() => {
                onRedoFieldChange?.();
              }}
            >
              <RedoOutlinedIcon className="!h-4.5 !w-4.5" />
            </IconButton>
          </span>
        </div>

        <HowToDrawCard />

        <div className="flex flex-col pt-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={handleFileSelection}
            disabled={!canReplaceDocument || isReplacingDocument}
          />
          <Button
            variant="text"
            color="primary"
            className="!justify-start !text-sm !font-medium !normal-case !w-fit"
            disabled={!canReplaceDocument || isReplacingDocument}
            onClick={() => { fileInputRef.current?.click(); }}
          >
            <CloudUploadRoundedIcon className="!h-4 !w-4 text-gray-500" />
            <span className="ml-2 text-secondary">
              {isReplacingDocument ? 'Replacing document...' : 'Replace document'}
            </span>
          </Button>
          <Button
            variant="text"
            color="primary"
            className="!justify-start !text-sm !font-medium !normal-case !w-fit"
            onClick={async () => {
              await downloadFileFromUrl(
                document.url,
                document.fileName || 'consent-form.pdf',
              );
            }}
          >
            <CloudDownloadRoundedIcon className="!h-4 !w-4 text-gray-500" />
            <span className="ml-2 text-secondary">Download PDF</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
